import { noop } from 'lodash';
import { History } from 'history';
import { parse, isAfter, isValid } from 'date-fns';
import {
  PricingTier,
  Product,
  ProductButtonConfig,
  ProductCode,
  ProductFamily,
  ServiceStatus,
  ServiceStatusInfo,
} from '../types';
import { Cart, Invoice } from '../features/self-serve/types/types';
import { getServiceStatusText } from './serviceStatus';
import { useNotifications } from '../../../_lib/notifications/useNotifications';
import { isNonSelfServeProductCode } from './productCodes';

interface ProductActivationState {
  isActivated: boolean;
  isInProgress: boolean;
  serviceStatusText: string;
  currentServiceStatusInfo?: ServiceStatusInfo;
  currentServiceStatus: ServiceStatus;
}

const CONTACT_SALES_TEXT = 'Contact Sales';
const ONE_PLAN_AT_A_TIME_MESSAGE = 'You can only activate one plan at a time';
const OUTSTANDING_INVOICE_MESSAGE =
  'You already have a pending invoice for this product. Please complete payment from the Invoices section.';

const getCurrentServiceStatusInfo = (
  product: Product,
  serviceStatusInfos: ServiceStatusInfo[]
): ServiceStatusInfo | undefined => {
  return serviceStatusInfos.find((info) => info.product.id === product.id);
};

export const getProductActivationState = (
  currentServiceStatusInfo?: ServiceStatusInfo
): ProductActivationState => {
  const isActivated = [
    ServiceStatus.Activated,
    ServiceStatus.Purchased,
    ServiceStatus.TestingCompleted,
  ].includes(currentServiceStatusInfo?.status);

  const isInProgress = [
    ServiceStatus.ActivationInProgress,
    ServiceStatus.TestingInProgress,
  ].includes(currentServiceStatusInfo?.status);

  const serviceStatusText = getServiceStatusText(
    currentServiceStatusInfo?.status
  );

  return {
    isActivated,
    isInProgress,
    serviceStatusText,
    currentServiceStatusInfo,
    currentServiceStatus:
      currentServiceStatusInfo?.status ?? ServiceStatus.NotActivated,
  };
};

const isProductInCart = (product: Product, cart: Cart | null): boolean => {
  return (
    cart?.cartItems?.some((item) => item.product.id === product.id) ?? false
  );
};

export const isEndDateInFuture = (
  serviceStatusInfo?: ServiceStatusInfo
): boolean => {
  if (!serviceStatusInfo) return false;

  // Skip VRA_PRO and CTS_BST products because they don't have an end date
  if (
    [ProductCode.VRA_PRO, ProductCode.CTS_BST].includes(
      serviceStatusInfo.product.code
    )
  ) {
    return true;
  }

  if (!serviceStatusInfo.endDate) {
    return false;
  }

  const refDate = new Date();
  const parsedEndDate = parse(serviceStatusInfo.endDate, 'yyyy-MM-dd', refDate);

  return isValid(parsedEndDate) && isAfter(parsedEndDate, refDate);
};

export const isProductActiveOrInProgress = (
  currentServiceStatusInfo?: ServiceStatusInfo
): boolean => {
  const activationState = getProductActivationState(currentServiceStatusInfo);
  const isEndDateInFutureValue = isEndDateInFuture(currentServiceStatusInfo);

  return (
    (activationState.isActivated && isEndDateInFutureValue) ||
    activationState.isInProgress
  );
};

const calculateCtsBstQuantity = (
  product: Product,
  selectedTier: PricingTier | null
): number => {
  const firstUpperBound =
    product.pricingData?.priceDetails.tiers?.[0]?.upperBound;
  const selectedUpperBound = selectedTier?.upperBound - 1;
  const fallbackQuantity = firstUpperBound ? firstUpperBound - 1 : 1;

  return selectedUpperBound ?? fallbackQuantity;
};

interface NonSelfServeConfigParams {
  product: Product;
  activationState: ProductActivationState;
  isEligibleForFreeCtaas: boolean;
  history: History;
  handleAddProductToOpportunity: (product: Product) => Promise<void>;
  isDisabled: boolean;
}

const getNonSelfServeButtonConfig = ({
  product,
  activationState,
  isEligibleForFreeCtaas,
  history,
  handleAddProductToOpportunity,
  isDisabled,
}: NonSelfServeConfigParams): ProductButtonConfig => {
  if (product.code === ProductCode.VRA_ELI) {
    return {
      text: product.callToActionText ?? CONTACT_SALES_TEXT,
      onClick: noop,
      disabled: true,
    };
  }

  if (activationState.isActivated || activationState.isInProgress) {
    return {
      text: activationState.serviceStatusText,
      onClick: () =>
        history.push({ pathname: '/customer/resilience/services' }),
      disabled: true,
    };
  }

  if (
    product.code === ProductCode.CTS_BST &&
    isEligibleForFreeCtaas &&
    activationState.currentServiceStatus === ServiceStatus.NotActivated
  ) {
    return {
      text: 'Activate',
      onClick: () => handleAddProductToOpportunity(product),
      disabled: isDisabled,
    };
  }

  if (activationState.currentServiceStatus === ServiceStatus.NotActivated) {
    return {
      text: product.callToActionText ?? CONTACT_SALES_TEXT,
      onClick: () => handleAddProductToOpportunity(product),
      disabled: isDisabled,
    };
  }

  return {
    text: activationState.serviceStatusText,
    onClick: () => history.push({ pathname: '/customer/resilience/services' }),
    disabled: true,
  };
};

interface SelfServeConfigParams {
  product: Product;
  productFamily: ProductFamily;
  activationState: ProductActivationState;
  cart: Cart | null;
  history: History;
  handleAddProductToCart: (
    cart: Cart | null,
    product: Product,
    quantity: number,
    hasConfirmedReplacement: boolean
  ) => Promise<void>;
  handleAddProductToOpportunity: (product: Product) => Promise<void>;
  isDisabled: boolean;
  quantity: number;
  selectedTier: PricingTier | null;
  notifications: ReturnType<typeof useNotifications>;
  serviceStatusInfos: ServiceStatusInfo[];
  invoices: Invoice[];
  products: Product[];
  isEligibleForFreeCtaas: boolean;
}

const getSelfServeButtonConfig = ({
  product,
  productFamily,
  activationState,
  cart,
  history,
  handleAddProductToCart,
  handleAddProductToOpportunity,
  isDisabled,
  quantity,
  selectedTier,
  notifications,
  serviceStatusInfos,
  invoices = [],
  products = [],
  isEligibleForFreeCtaas,
}: SelfServeConfigParams): ProductButtonConfig => {
  const productInCart = isProductInCart(product, cart);

  if (isNonSelfServeProductCode(product.code)) {
    if (activationState.currentServiceStatus === ServiceStatus.NotActivated) {
      if (product.code === ProductCode.CTS_BST && isEligibleForFreeCtaas) {
        return {
          text: 'Activate',
          onClick: () => handleAddProductToOpportunity(product),
          disabled: isDisabled,
        };
      }

      return {
        text: product.callToActionText ?? CONTACT_SALES_TEXT,
        onClick: () => handleAddProductToOpportunity(product),
        disabled: isDisabled,
      };
    }

    return {
      text: activationState.serviceStatusText,
      onClick: () =>
        history.push({ pathname: '/customer/resilience/services' }),
      disabled: true,
    };
  }

  if (productInCart) {
    return {
      text: 'Go to Cart',
      onClick: () => history.push({ pathname: '/customer/resilience/cart' }),
      disabled: isDisabled,
    };
  }

  if (product.code === ProductCode.VRA_ELI) {
    return {
      text: product.callToActionText ?? CONTACT_SALES_TEXT,
      onClick: noop,
      disabled: true,
    };
  }

  if (
    (activationState.isActivated || activationState.isInProgress) &&
    product.code === ProductCode.VRA_PRO
  ) {
    return {
      text: activationState.serviceStatusText,
      onClick: () =>
        history.push({ pathname: '/customer/resilience/services' }),
      disabled: isDisabled,
    };
  }

  if (
    activationState.currentServiceStatus ===
      ServiceStatus.ActivationInProgress ||
    (activationState.currentServiceStatus === ServiceStatus.Activated &&
      isEndDateInFuture(activationState.currentServiceStatusInfo))
  ) {
    return {
      text: activationState.serviceStatusText,
      onClick: () =>
        history.push({ pathname: '/customer/resilience/services' }),
      disabled: true,
    };
  }

  const hasAnyActiveOrActivation = serviceStatusInfos
    .filter((info) => info.productFamily.code === productFamily.code)
    .some(
      (info) =>
        info.status === ServiceStatus.ActivationInProgress ||
        (info.status === ServiceStatus.Activated && isEndDateInFuture(info))
    );

  if (hasAnyActiveOrActivation) {
    return {
      text: 'Add to Cart',
      onClick: async () => {
        notifications.error(ONE_PLAN_AT_A_TIME_MESSAGE);
      },
      helperText: ONE_PLAN_AT_A_TIME_MESSAGE,
      disabled: true,
    };
  }

  const hasOutstandingInvoice = invoices
    .filter((invoice) => !invoice.status?.toLowerCase()?.includes('paid'))
    .some((invoice) =>
      invoice.invoiceLineItems.some((lineItem) =>
        products.some((_product) => lineItem.productId === _product.id)
      )
    );

  if (hasOutstandingInvoice) {
    return {
      text: 'Add to Cart',
      onClick: async () => {
        notifications.error(OUTSTANDING_INVOICE_MESSAGE);
      },
      helperText: OUTSTANDING_INVOICE_MESSAGE,
      disabled: true,
    };
  }

  return {
    text: 'Add to Cart',
    onClick: async () => {
      if (quantity < 1) {
        notifications.error('Quantity is required and must be at least 1');
        return;
      }

      if (product.code === ProductCode.CTS_BST) {
        const calculatedQuantity = calculateCtsBstQuantity(
          product,
          selectedTier
        );

        await handleAddProductToCart(cart, product, calculatedQuantity, false);

        return;
      }

      await handleAddProductToCart(cart, product, quantity, false);
    },
    disabled: isDisabled,
  };
};

interface GetProductButtonConfigProps {
  product?: Product;
  productFamily?: ProductFamily;
  isPoolTenant: boolean;
  crsEnableSelfServe: boolean;
  serviceStatusInfos?: ServiceStatusInfo[];
  cart: Cart | null;
  isEligibleForFreeCtaas: boolean;
  history: History;
  handleAddProductToCart: (
    cart: Cart | null,
    product: Product,
    quantity: number,
    hasConfirmedReplacement: boolean
  ) => Promise<void>;
  handleAddProductToOpportunity: (product: Product) => Promise<void>;
  notifications: ReturnType<typeof useNotifications>;
  isDisabled: boolean;
  quantity: number;
  selectedTier: PricingTier | null;
  invoices: Invoice[];
  products: Product[];
}

export const getProductButtonConfig = ({
  product,
  productFamily,
  isPoolTenant = false,
  crsEnableSelfServe,
  serviceStatusInfos = [],
  cart,
  isEligibleForFreeCtaas = false,
  history,
  handleAddProductToCart,
  handleAddProductToOpportunity,
  notifications,
  isDisabled,
  quantity,
  selectedTier,
  invoices = [],
  products = [],
}: GetProductButtonConfigProps): ProductButtonConfig => {
  if (!product || !productFamily) {
    return {
      text: 'Loading...',
      onClick: noop,
      disabled: true,
    };
  }

  if (isPoolTenant) {
    return {
      text: 'View Details',
      onClick: () => {
        notifications.show({
          type: 'info',
          title: 'Information Only',
          body: 'This catalog is for informational purposes only. Contact your administrator for service activation.',
          position: 'top-right',
        });
      },
      disabled: false,
    };
  }

  const currentServiceStatusInfo = getCurrentServiceStatusInfo(
    product,
    serviceStatusInfos
  );
  const activationState = getProductActivationState(currentServiceStatusInfo);

  if (!crsEnableSelfServe) {
    return getNonSelfServeButtonConfig({
      product,
      activationState,
      isEligibleForFreeCtaas,
      history,
      handleAddProductToOpportunity,
      isDisabled,
    });
  }

  return getSelfServeButtonConfig({
    product,
    productFamily,
    activationState,
    cart,
    history,
    handleAddProductToCart,
    handleAddProductToOpportunity,
    isDisabled,
    quantity,
    selectedTier,
    notifications,
    serviceStatusInfos,
    invoices,
    products,
    isEligibleForFreeCtaas,
  });
};
